import { CART_KEY } from '@/lib/constants';
import { useLocalStorage } from '@/lib/use-local-storage';
import { verifiedResponseAtom } from '@/store/checkout';
import { useAtom } from 'jotai';
import React, { useCallback } from 'react';
import { State, cartReducer, initialState } from './cart.reducer';
import { Item, PriceChange, getItem, inStock } from './cart.utils';

// Define a name for your broadcast channel
const CART_BROADCAST_CHANNEL = 'cart-sync-channel';

interface CartProviderState extends State {
  addItemsToCart: (items: Item[]) => void;
  addItemToCart: (item: Item, quantity: number) => void;
  removeItemFromCart: (id: Item['id']) => void;
  clearItemFromCart: (id: Item['id']) => void;
  getItemFromCart: (id: Item['id']) => any | undefined;
  isInCart: (id: Item['id']) => boolean;
  isInStock: (id: Item['id']) => boolean;
  resetCart: () => void;
  updateCartLanguage: (language: string) => void;
  updateCartPrices: (changes: PriceChange[]) => void;
}
export const cartContext = React.createContext<CartProviderState | undefined>(
  undefined,
);

cartContext.displayName = 'CartContext';

export const useCart = () => {
  const context = React.useContext(cartContext);
  if (context === undefined) {
    throw new Error(`useCart must be used within a CartProvider`);
  }
  return React.useMemo(() => context, [context]);
};

export const CartProvider: React.FC<{ children?: React.ReactNode }> = (
  props,
) => {
  const [savedCart, saveCart] = useLocalStorage(
    CART_KEY,
    JSON.stringify(initialState),
  );
  const [state, dispatch] = React.useReducer(
    cartReducer,
    savedCart ? JSON.parse(savedCart) : initialState,
  );
  const [, emptyVerifiedResponse] = useAtom(verifiedResponseAtom);

  // Create a single BroadcastChannel instance
  const channel = React.useMemo(
    () => new BroadcastChannel(CART_BROADCAST_CHANNEL),
    [],
  );

  React.useEffect(() => {
    emptyVerifiedResponse(null);
  }, [emptyVerifiedResponse, state]);

  React.useEffect(() => {
    // Save state to localStorage and broadcast the change
    saveCart(JSON.stringify(state));
    try {
      // Use structuredClone to safely pass the entire state object
      channel.postMessage(structuredClone(state));
    } catch (e) {
      console.error('Failed to broadcast cart state:', e);
    }
  }, [state, saveCart, channel]);

  React.useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      dispatch(event.data);
    };

    channel.addEventListener('message', handleMessage);

    return () => {
      channel.removeEventListener('message', handleMessage);
    };
  }, [channel]);

  const dispatchAndBroadcast = (action: any) => {
    dispatch(action);

    try {
      channel.postMessage(action);
    } catch (e) {
      console.error('Failed to broadcast action:', e);
    }
  };

  const addItemsToCart = (items: Item[]) =>
    dispatchAndBroadcast({ type: 'ADD_ITEMS_WITH_QUANTITY', items });
  const addItemToCart = (item: Item, quantity: number) =>
    dispatchAndBroadcast({ type: 'ADD_ITEM_WITH_QUANTITY', item, quantity });
  const removeItemFromCart = (id: Item['id']) =>
    dispatchAndBroadcast({ type: 'REMOVE_ITEM_OR_QUANTITY', id });
  const clearItemFromCart = (id: Item['id']) =>
    dispatchAndBroadcast({ type: 'REMOVE_ITEM', id });
  const isInCart = useCallback(
    (id: Item['id']) => !!getItem(state.items, id),
    [state.items],
  );
  const getItemFromCart = useCallback(
    (id: Item['id']) => getItem(state.items, id),
    [state.items],
  );
  const isInStock = useCallback(
    (id: Item['id']) => inStock(state.items, id),
    [state.items],
  );
  const updateCartLanguage = (language: string) =>
    dispatchAndBroadcast({ type: 'UPDATE_CART_LANGUAGE', language });
  const resetCart = () => dispatchAndBroadcast({ type: 'RESET_CART' });
  const updateCartPrices = (changes: PriceChange[]) =>
    dispatchAndBroadcast({ type: 'UPDATE_ITEM_PRICES', changes });
  const value = React.useMemo(
    () => ({
      ...state,
      addItemsToCart,
      addItemToCart,
      removeItemFromCart,
      clearItemFromCart,
      getItemFromCart,
      isInCart,
      isInStock,
      resetCart,
      updateCartLanguage,
      updateCartPrices,
    }),
    [getItemFromCart, isInCart, isInStock, state],
  );
  return <cartContext.Provider value={value} {...props} />;
};
